import { Component } from '@theme/component';
import { ThemeEvents, CartAddEvent } from '@theme/events';
import { ADD_TO_CART_TEXT_ANIMATION_DURATION } from '@theme/product-form';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, onAnimationEnd, isMobileBreakpoint, isDesktopBreakpoint } from '@theme/utilities';

/**
 * Syncs data-option-available and aria-disabled from a response variant-picker to DOM inputs.
 * @param {Element} sourcePicker - The variant-picker from the fetch response
 * @param {Element} targetPicker - The variant-picker in the DOM to update
 */
function syncAvailability(sourcePicker, targetPicker) {
  const availabilityMap = new Map();
  for (const input of sourcePicker.querySelectorAll('input[data-option-value-id]')) {
    const id = /** @type {HTMLElement} */ (input).dataset.optionValueId;
    const available = /** @type {HTMLElement} */ (input).dataset.optionAvailable;
    if (id && available !== undefined) availabilityMap.set(id, available);
  }
  for (const input of targetPicker.querySelectorAll('input[data-option-value-id]')) {
    const id = /** @type {HTMLElement} */ (input).dataset.optionValueId;
    if (!id || !availabilityMap.has(id)) continue;
    /** @type {HTMLElement} */ (input).dataset.optionAvailable = /** @type {string} */ (availabilityMap.get(id));
    if (availabilityMap.get(id) === 'false') {
      input.setAttribute('aria-disabled', 'true');
    } else {
      input.removeAttribute('aria-disabled');
    }
  }
}

/**
 * Quick add block component.
 * @typedef {object} Refs
 * @property {HTMLElement} [overlay]
 * @property {HTMLElement} [overlayContent]
 * @property {HTMLButtonElement} [triggerOpen]
 * @extends {Component<Refs>}
 */
export class QuickAddBlock extends Component {
  #overlayAbortController = null;

  #fetchAbortController = null;

  #hoverFocusAbortController = null;

  #productCard = null;

  #loadedProductId = null;

  #overlayOpenIntent = false;

  get quickAddStyle() {
    return this.dataset.quickAddStyle || 'modal';
  }

  get overlayDesktopBehavior() {
    return this.dataset.overlayDesktopBehavior || 'click_toggle';
  }

  connectedCallback() {
    super.connectedCallback();
    mediaQueryLarge.addEventListener('change', this.#closeOnViewportChange);

    const productCard = this.closest('product-card');
    if (productCard) {
      this.#productCard = productCard;
      productCard.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUpdate);
    }

    this.#loadedProductId = this.dataset.productId || null;

    if (this.quickAddStyle === 'overlay' && this.overlayDesktopBehavior === 'hover_focus') {
      this.#attachHoverFocusListeners();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#closeOnViewportChange);
    this.#overlayAbortController?.abort();
    this.#fetchAbortController?.abort();
    this.#hoverFocusAbortController?.abort();
    this.#productCard?.removeEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUpdate);
  }

  /**
   * Handles card-level swatch changes: close overlay, sync product context, and update forms.
   * @param {CustomEvent} event - The variant:update event from the swatch picker
   */
  #handleCardVariantUpdate = (event) => {
    if (this.contains(/** @type {Node} */ (event.target))) return;

    this.closeOverlay();
    this.#syncProductContext();

    const variant = event.detail?.resource;
    if (variant) {
      this.#syncFormsToVariant({ id: variant.id, available: variant.available });
    }

    this.#fetchAndSyncContent();
  };

  /** Syncs modal/overlay forms to the currently checked swatch on the product card. */
  #syncToCurrentSwatch() {
    if (!this.#productCard) return;

    const checkedSwatch = /** @type {HTMLInputElement | null} */ (
      this.#productCard.querySelector('swatches-variant-picker-component input[type="radio"]:checked')
    );
    if (!checkedSwatch) return;

    this.#syncProductContext();

    const variantId = checkedSwatch.dataset.firstAvailableOrFirstVariantId || checkedSwatch.dataset.variantId;
    const available = checkedSwatch.dataset.optionAvailable !== 'false';

    if (variantId) {
      this.#syncFormsToVariant({ id: variantId, available });
    }

    this.#fetchAndSyncContent();
  }

  /** Syncs product URL/ID from the card's swatch to this block, its variant pickers, and product-form components. */
  #syncProductContext() {
    if (!this.#productCard) return;

    const cardSwatchPicker = this.#productCard.querySelector('swatches-variant-picker-component');
    if (!cardSwatchPicker) return;

    const newProductUrl = /** @type {HTMLElement} */ (cardSwatchPicker).dataset.productUrl;
    const newProductId = /** @type {HTMLElement} */ (cardSwatchPicker).dataset.productId;
    if (!newProductUrl) return;

    this.dataset.productUrl = newProductUrl;

    for (const picker of this.querySelectorAll('variant-picker')) {
      /** @type {HTMLElement} */ (picker).dataset.productUrl = newProductUrl;
      if (newProductId) /** @type {HTMLElement} */ (picker).dataset.productId = newProductId;
    }

    if (newProductId) {
      for (const form of this.querySelectorAll('product-form-component')) {
        /** @type {HTMLElement} */ (form).dataset.productId = newProductId;
      }
    }
  }

  /**
   * Updates all quick-add forms to reflect the given variant.
   * @param {{ id: string | number, available: boolean }} variant
   */
  #syncFormsToVariant(variant) {
    const variantInputs = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('input[name="id"][ref="variantId"]')
    );

    for (const input of variantInputs) {
      input.value = String(variant.id);
      input.setAttribute('value', String(variant.id));
      if (variant.available === false) {
        input.setAttribute('disabled', '');
      } else {
        input.removeAttribute('disabled');
      }
    }

    const addToCartContainers = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('add-to-cart-component'));

    for (const container of addToCartContainers) {
      if (variant.available === false) {
        if (typeof (/** @type {any} */ (container).disable) === 'function') {
          /** @type {any} */ (container).disable();
        }
      } else {
        if (typeof (/** @type {any} */ (container).enable) === 'function') {
          /** @type {any} */ (container).enable();
        }
      }
    }

    const submitButtons = /** @type {NodeListOf<HTMLButtonElement>} */ (
      this.querySelectorAll('button[name="add"][type="submit"]')
    );
    for (const button of submitButtons) {
      button.disabled = variant.available === false;
    }
  }

  /**
   * Collects the option value IDs for the fetch. The color comes from the card swatch (the
   * surface excludes the swatch axis); other options come from the variant-picker when present.
   * @param {HTMLElement | null} picker
   * @param {Element | null} cardSwatchPicker
   * @returns {string[]}
   */
  #collectSelectedOptionValueIds(picker, cardSwatchPicker) {
    const ids = [];

    const checkedSwatch = /** @type {HTMLInputElement | null} */ (
      cardSwatchPicker?.querySelector('input[type="radio"]:checked') ?? null
    );
    if (checkedSwatch?.dataset.optionValueId) ids.push(checkedSwatch.dataset.optionValueId);

    if (picker) {
      for (const input of picker.querySelectorAll('fieldset input:checked')) {
        const id = /** @type {HTMLElement} */ (input).dataset.optionValueId;
        if (id && !ids.includes(id)) ids.push(id);
      }
    }

    return ids;
  }

  /**
   * @returns {boolean}
   */
  #hasSurface() {
    return Boolean(this.querySelector('quick-add-dialog') || this.refs.overlay);
  }

  /** Fetches fresh content from section-rendering-quick-add and updates the modal/overlay. */
  #fetchAndSyncContent() {
    if (!this.#productCard) return;
    if (!this.#hasSurface()) return;

    const variantPickers = this.querySelectorAll('variant-picker');
    const firstPicker = /** @type {HTMLElement | null} */ (variantPickers[0] ?? null);
    const productUrl = this.dataset.productUrl;
    if (!productUrl) return;
    this.#setTapChipsSyncing(true);

    const cardSwatchPicker = this.#productCard.querySelector('swatches-variant-picker-component');
    const currentProductId = cardSwatchPicker ? /** @type {HTMLElement} */ (cardSwatchPicker).dataset.productId : null;
    const isProductChanging = currentProductId && currentProductId !== this.#loadedProductId;

    const optionValueIds = isProductChanging ? [] : this.#collectSelectedOptionValueIds(firstPicker, cardSwatchPicker);

    const params = optionValueIds.length ? `option_values=${optionValueIds.join(',')}` : '';
    const requestUrl = `${productUrl.split('?')[0]}?section_id=section-rendering-quick-add&${params}`;

    this.#fetchAbortController?.abort();
    this.#fetchAbortController = new AbortController();

    fetch(requestUrl, { signal: this.#fetchAbortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const newPicker = html.querySelector('variant-picker');

        // newPicker is always present in the response; fall back to swatch-derived id.
        const newProductId =
          /** @type {HTMLElement | null} */ (newPicker)?.dataset.productId || currentProductId || null;
        const isProductChange = newProductId && newProductId !== this.#loadedProductId;

        if (html.querySelector('quick-add-tap-chips')) {
          this.#replaceTapChipsContent(this.querySelectorAll('quick-add-tap-chips'), html);
        }
        this.#setTapChipsSyncing(false);

        if (isProductChange) {
          if (newPicker && variantPickers.length > 0) {
            this.#replaceVariantPickerContent(variantPickers, newPicker);
          }
          this.#replaceTitleContent(html);
          this.#patchWishlistProductIds(newProductId);
        } else if (newPicker && variantPickers.length > 0) {
          this.#updateAvailability(variantPickers, newPicker);
        }

        const newScript = newPicker?.querySelector('script[type="application/json"]');
        if (newScript?.textContent) {
          for (const vp of variantPickers) {
            const existingScript = vp.querySelector('script[type="application/json"]');
            if (existingScript) {
              existingScript.textContent = newScript.textContent;
            }
          }

          try {
            const variantData = JSON.parse(newScript.textContent);
            this.#syncFormsToVariant({ id: variantData.id, available: variantData.available });
          } catch {
            // Variant JSON parse failed — form keeps existing value
          }
        }

        if (newProductId) {
          this.#loadedProductId = newProductId;
        }

        const newNotification = html.querySelector('[ref="cartNotificationContent"]');
        if (newNotification) {
          const templates = this.querySelectorAll('[ref="cartNotificationContent"]');
          for (const tpl of templates) {
            tpl.innerHTML = newNotification.innerHTML;
          }
        }

        const newPrice = html.querySelector('[data-quick-add-price] [ref="priceContainer"]');
        if (newPrice) {
          const priceBlocks = this.querySelectorAll('.quick-add-price[data-selected-variant-price="true"]');
          for (const block of priceBlocks) {
            const container = block.querySelector('[ref="priceContainer"]');
            if (container) container.innerHTML = newPrice.innerHTML;
          }
        }

        for (const image of this.querySelectorAll('quick-add-image')) {
          image.updateFromHtml?.(html);
        }
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          this.#setTapChipsSyncing(false);
          console.error('[quick-add] fetchAndSyncContent: fetch failed', error);
        }
      });
  }

  /**
   * Updates variant-picker content for a combined listing product change.
   * @param {NodeListOf<Element>} variantPickers - Existing variant pickers in the DOM
   * @param {Element} newPicker - The variant-picker from the fetch response
   */
  #replaceVariantPickerContent(variantPickers, newPicker) {
    const newFieldsets = newPicker.querySelectorAll('fieldset.variant-option');
    const optionDataByPosition = new Map();

    for (const fs of newFieldsets) {
      const position = /** @type {HTMLElement} */ (fs).dataset.optionPosition;
      if (!position) continue;
      const valueMap = new Map();
      for (const input of fs.querySelectorAll('input')) {
        const valueName = /** @type {HTMLInputElement} */ (input).value;
        const optionValueId = /** @type {HTMLElement} */ (input).dataset.optionValueId || '';
        const available = /** @type {HTMLElement} */ (input).dataset.optionAvailable || 'false';
        const variantId = /** @type {HTMLElement} */ (input).dataset.variantId || '';
        const checked = /** @type {HTMLInputElement} */ (input).hasAttribute('checked');
        valueMap.set(valueName, { optionValueId, available, variantId, checked });
      }
      optionDataByPosition.set(position, valueMap);
    }

    for (const vp of variantPickers) {
      const visibleFieldsets = vp.querySelectorAll('fieldset.variant-option');
      for (const fs of visibleFieldsets) {
        const position = /** @type {HTMLElement} */ (fs).dataset.optionPosition;
        if (!position) continue;
        const valueMap = optionDataByPosition.get(position);
        if (!valueMap) continue;

        for (const input of fs.querySelectorAll('input[data-option-value-id]')) {
          const valueName = /** @type {HTMLInputElement} */ (input).value;
          const newData = valueMap.get(valueName);
          if (newData) {
            /** @type {HTMLElement} */ (input).dataset.optionValueId = newData.optionValueId;
            /** @type {HTMLElement} */ (input).dataset.optionAvailable = newData.available;
            if (newData.variantId) {
              /** @type {HTMLElement} */ (input).dataset.variantId = newData.variantId;
            } else {
              delete (/** @type {HTMLElement} */ (input).dataset.variantId);
            }
            if (newData.available === 'false') {
              /** @type {HTMLInputElement} */ (input).setAttribute('aria-disabled', 'true');
            } else {
              /** @type {HTMLInputElement} */ (input).removeAttribute('aria-disabled');
            }
            /** @type {HTMLInputElement} */ (input).checked = newData.checked;
            if (newData.checked) {
              input.setAttribute('checked', '');
            } else {
              input.removeAttribute('checked');
            }
          }
        }
      }
    }
  }

  /**
   * Updates availability on existing variant-picker inputs (same product, just different swatch).
   * @param {NodeListOf<Element>} variantPickers - Existing variant pickers in the DOM
   * @param {Element} newPicker - The variant-picker from the fetch response
   */
  #updateAvailability(variantPickers, newPicker) {
    for (const vp of variantPickers) {
      syncAvailability(newPicker, vp);
    }
  }

  /**
   * Replaces innerHTML of every live <quick-add-tap-chips> element inside the surface with the chips parsed from the section-rendering response.
   * @param {NodeListOf<Element>} existingChips - Live tap-chip elements in the surface
   * @param {Document} html - Parsed section-rendering response
   */
  #replaceTapChipsContent(existingChips, html) {
    if (existingChips.length === 0) return;
    const newChips = html.querySelector('quick-add-tap-chips');
    if (!newChips) return;
    for (const el of existingChips) {
      el.innerHTML = newChips.innerHTML;
      const tapPosition = newChips.getAttribute('data-tap-option-position');
      const swatchPosition = newChips.getAttribute('data-swatch-option-position');
      if (tapPosition) el.setAttribute('data-tap-option-position', tapPosition);
      if (swatchPosition) {
        el.setAttribute('data-swatch-option-position', swatchPosition);
      } else {
        el.removeAttribute('data-swatch-option-position');
      }
    }
  }

  /** @param {boolean} syncing */
  #setTapChipsSyncing(syncing) {
    for (const el of this.querySelectorAll('quick-add-tap-chips')) {
      el.toggleAttribute('aria-busy', syncing);
    }
  }

  /**
   * Replaces the text of every `.quick-add-title` element inside the surface with the product title from the section-rendering response.
   * @param {Document} html - Parsed section-rendering response
   */
  #replaceTitleContent(html) {
    const source = html.querySelector('[data-quick-add-title]');
    if (!source) return;
    const newTitle = source.textContent?.trim();
    if (!newTitle) return;
    for (const titleEl of this.querySelectorAll('.quick-add-title')) {
      titleEl.textContent = newTitle;
    }
  }

  /**
   * Patches `data-product-id` on every wishlist-button inside the surface so each button refreshes its pressed state.
   * @param {string | null} newProductId
   */
  #patchWishlistProductIds(newProductId) {
    if (!newProductId) return;
    for (const button of this.querySelectorAll('wishlist-button')) {
      /** @type {HTMLElement} */ (button).dataset.productId = newProductId;
    }
  }

  /**
   * Handles quick add button click (for multi-variant products)
   * @param {Event} event
   */
  handleClick = (event) => {
    event.preventDefault();

    if (this.quickAddStyle === 'overlay') {
      if (isDesktopBreakpoint()) {
        this.#toggleOverlay();
        return;
      }
    }

    this.#openModal();
  };

  /** Opens the per-card modal dialog */
  #openModal() {
    const dialogComponent = this.querySelector('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#syncToCurrentSwatch();
    this.#stayVisibleUntilDialogCloses(dialogComponent);
    dialogComponent.showDialog();
  }

  #openOverlay() {
    const overlay = /** @type {HTMLElement | undefined} */ (this.refs.overlay);
    if (!overlay) return;

    this.#overlayOpenIntent = true;

    const wasClosing = overlay.classList.contains('quick-add-overlay--closing');
    if (wasClosing) {
      overlay.classList.remove('quick-add-overlay--closing');
    } else {
      if (!overlay.hidden) return;
      this.#syncToCurrentSwatch();
      overlay.hidden = false;
    }

    this.toggleAttribute('stay-visible', true);

    this.#overlayAbortController?.abort();
    this.#overlayAbortController = new AbortController();
    const { signal } = this.#overlayAbortController;

    const overlayContent = overlay.querySelector('.quick-add-overlay-content');
    overlayContent?.addEventListener('click', (/** @type {MouseEvent} */ e) => e.stopPropagation(), { signal });

    overlay.addEventListener(
      ThemeEvents.cartUpdate,
      (/** @type {CustomEvent} */ e) => {
        if (!e.detail?.data?.didError) {
          this.#overlayOpenIntent = false;
          this.closeOverlay();
        }
      },
      { signal },
    );

    document.addEventListener(
      'keydown',
      (/** @type {KeyboardEvent} */ e) => {
        if (e.key === 'Escape') {
          this.#overlayOpenIntent = false;
          this.closeOverlay();
        }
      },
      { signal },
    );
  }

  #toggleOverlay() {
    const overlay = /** @type {HTMLElement | undefined} */ (this.refs.overlay);
    if (!overlay) return;

    if (!overlay.hidden && !overlay.classList.contains('quick-add-overlay--closing')) {
      this.#overlayOpenIntent = false;
      this.closeOverlay();
      return;
    }

    this.#openOverlay();

    const abortController = this.#overlayAbortController;
    requestAnimationFrame(() => {
      document.addEventListener(
        'click',
        (/** @type {MouseEvent} */ e) => {
          if (!overlay.contains(/** @type {Node} */ (e.target))) {
            this.#overlayOpenIntent = false;
            this.closeOverlay();
          }
        },
        { signal: abortController?.signal },
      );
    });
  }

  /**
   * Attaches mouseenter/mouseleave/focusin/focusout listeners for the hover_focus
   * desktop behavior mode. Listens on the <product-card> element, not the media
   * wrapper: the media sits inside .product-card__content, which is
   * pointer-events: none (right-click passthrough, #662), so mouse events never
   * reach it — the <product-card> element still receives them.
   */
  #attachHoverFocusListeners() {
    const hoverTarget = this.#productCard ?? this.parentElement;
    if (!hoverTarget) return;

    this.#hoverFocusAbortController?.abort();
    this.#hoverFocusAbortController = new AbortController();
    const { signal } = this.#hoverFocusAbortController;

    hoverTarget.addEventListener('mouseenter', this.#handleCardMouseEnter, { signal });
    hoverTarget.addEventListener('mouseleave', this.#handleCardMouseLeave, { signal });
    hoverTarget.addEventListener('focusin', this.#handleCardFocusIn, { signal });
    hoverTarget.addEventListener('focusout', this.#handleCardFocusOut, { signal });
  }

  #handleCardMouseEnter = () => {
    if (isMobileBreakpoint()) return;
    this.#overlayOpenIntent = true;
    this.#openOverlay();
  };

  #handleCardMouseLeave = () => {
    if (isMobileBreakpoint()) return;
    this.#overlayOpenIntent = false;
    this.closeOverlay();
  };

  #handleCardFocusIn = () => {
    if (isMobileBreakpoint()) return;
    this.#overlayOpenIntent = true;
    this.#openOverlay();
  };

  /** @param {FocusEvent} event */
  #handleCardFocusOut = (event) => {
    if (isMobileBreakpoint()) return;
    const hoverTarget = this.#productCard ?? this.parentElement;
    if (hoverTarget?.contains(/** @type {Node | null} */ (event.relatedTarget))) return;
    this.#overlayOpenIntent = false;
    this.closeOverlay();
  };

  closeOverlay = async () => {
    const overlay = /** @type {HTMLElement | undefined} */ (this.refs.overlay);
    if (!overlay || overlay.hidden) return;
    if (overlay.dataset.pinned === 'true') return;
    if (this.#overlayOpenIntent) return;
    if (overlay.classList.contains('quick-add-overlay--closing')) return;

    this.#overlayAbortController?.abort();
    this.#overlayAbortController = null;

    overlay.classList.add('quick-add-overlay--closing');

    await onAnimationEnd(overlay, undefined, { subtree: false });

    if (this.#overlayOpenIntent) {
      this.#openOverlay();
      return;
    }

    if (this.contains(document.activeElement)) {
      this.refs.triggerOpen?.focus();
    }

    overlay.hidden = true;
    overlay.classList.remove('quick-add-overlay--closing');
    this.toggleAttribute('stay-visible', false);
  };

  /** @param {'overlay' | 'modal' | 'drawer'} type */
  surface(type) {
    if (type === 'overlay') {
      return /** @type {HTMLElement | null} */ (this.refs.overlay ?? null);
    }
    return /** @type {HTMLElement | null} */ (this.querySelector('quick-add-dialog'));
  }

  /** @param {'overlay' | 'modal' | 'drawer'} type */
  openSurface(type) {
    if (type === 'overlay') {
      const overlay = /** @type {HTMLElement | undefined} */ (this.refs.overlay);
      if (!overlay || !overlay.hidden) return;
      this.#openOverlay();
      return;
    }

    const dialogComponent = this.querySelector('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.dataset.designModeSurface = type;
    if (!dialogComponent.refs.dialog?.open) {
      this.#stayVisibleUntilDialogCloses(dialogComponent);
      dialogComponent.showDialog();
    }
  }

  /**
   * Keeps the quick-add button visible while the dialog is open
   * @param {QuickAddDialog} dialogComponent
   */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  /**
   * Close modal/overlay when viewport crosses the lg breakpoint
   */
  #closeOnViewportChange = () => {
    const dialogComponent = this.querySelector('quick-add-dialog');
    if (dialogComponent instanceof QuickAddDialog && dialogComponent.dataset.pinned !== 'true') {
      dialogComponent.closeDialog();
    }

    this.closeOverlay();
  };
}

if (!customElements.get('quick-add-block')) {
  customElements.define('quick-add-block', QuickAddBlock);
}

/**
 * Quick add dialog component.
 * Per-card dialog that auto-closes on cart success and updates links on variant change.
 *
 * @extends {DialogComponent}
 */
class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, {
      signal: this.#abortController.signal,
    });
    // Stop variant selected events from reaching product-card (which would try to sync to swatches)
    this.addEventListener(ThemeEvents.variantSelected, this.#stopPropagation, {
      signal: this.#abortController.signal,
    });
    // Stop click events from reaching product-card (which would navigate to PDP)
    this.addEventListener('click', this.#stopPropagation, {
      signal: this.#abortController.signal,
    });

    this.addEventListener('click', this.#swallowWhenPinned, {
      capture: true,
      signal: this.#abortController.signal,
    });
    this.addEventListener('keydown', this.#swallowWhenPinned, {
      capture: true,
      signal: this.#abortController.signal,
    });
  }

  /** @param {Event} event */
  #swallowWhenPinned = (event) => {
    if (this.dataset.pinned !== 'true') return;
    if (event instanceof KeyboardEvent && event.key !== 'Escape') return;
    event.stopImmediatePropagation();
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @param {Event} event
   */
  #stopPropagation = (event) => {
    event.stopPropagation();
  };

  /**
   * Close dialog immediately on successful cart add.
   * Must close synchronously (before the event reaches CartNotification on document)
   * because dialog.close() triggers light-dismiss of popover="auto" elements.
   * @param {CustomEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.detail?.data?.didError) return;
    if (this.dataset.pinned === 'true') return;

    const { dialog } = this.refs;
    if (!dialog.open) return;

    // Stop the event — we'll re-dispatch it after the dialog is closed
    event.stopPropagation();

    // Close dialog synchronously (before popover shows) to avoid light-dismiss
    // Don't restore scroll — let CartNotification.scrollToAnchor handle it (scrolls to top)
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';

    dialog.close();
    this.dispatchEvent(new DialogCloseEvent());

    // Re-dispatch from the original source so cart-icon, cart-drawer, cart-notification all receive it
    const source = /** @type {Element | null} */ (event.target);
    if (source) {
      source.dispatchEvent(new CartAddEvent(event.detail.resource, event.detail.sourceId, event.detail.data));
    }
  };

  /**
   * Sync size availability into the modal on variant change.
   *
   * The "See full details" link deliberately keeps the bare product URL: a `?variant=` param
   * sets product.selected_variant on the PDP, switching off the forced size selection.
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    // Stop variant:update from reaching ProductCard — modal variant changes should not
    // trigger card-level handlers which expect section-rendering-product-card HTML.
    event.stopPropagation();

    // Sync size availability from response — variant picker skips morph in quick-add context
    const html = event.detail.data?.html;
    if (html) {
      const newPicker = html.querySelector('variant-picker');
      const picker = this.querySelector('variant-picker');
      if (newPicker && picker) syncAvailability(newPicker, picker);
    }
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}

/**
 * Tap-to-add chip component.
 * Each chip directly adds its variant to the cart when tapped.
 *
 * Uses an optimistic feedback pattern (mirrors `AddToCartComponent`): on click,
 * the chip swaps to a checkmark synchronously and auto-resets after
 * ADD_TO_CART_TEXT_ANIMATION_DURATION, regardless of the cart fetch outcome.
 * The global cart error UI handles real failures; a `cart:error` listener
 * rolls back the optimistic check if the server rejects the add.
 *
 * @extends {Component<{}>}
 */
class QuickAddTapChips extends Component {
  /** @type {HTMLButtonElement | null} */
  #activeChip = null;

  /** @type {number | undefined} */
  #successTimeout;

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener('click', this.#handleClick, { signal });

    const formComponent = this.closest('product-form-component');
    if (formComponent) {
      formComponent.addEventListener(ThemeEvents.cartError, this.#handleCartError, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
    if (this.#successTimeout) clearTimeout(this.#successTimeout);
  }

  /**
   * Handles chip button click: sets the variant ID on the parent form, submits it,
   * and shows the success state immediately (optimistic UI).
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (this.getAttribute('aria-busy') === 'true') return;

    const chip = /** @type {HTMLButtonElement | null} */ (/** @type {Element} */ (event.target).closest('.tap-chip'));
    if (!chip || chip.disabled || chip.classList.contains('tap-chip--success')) return;

    const variantId = chip.dataset.variantId;
    if (!variantId) return;

    const form = /** @type {HTMLFormElement | null} */ (this.closest('form'));
    if (!form) return;

    // Update the hidden variant ID input so the form submits the correct variant
    const variantInput = /** @type {HTMLInputElement | null} */ (
      form.querySelector('input[name="id"][ref="variantId"]')
    );
    if (variantInput) {
      variantInput.value = variantId;
      variantInput.removeAttribute('disabled');
    }

    this.#setChipSuccess(chip);
    form.requestSubmit();
  };

  /**
   * Rolls back the optimistic success state if the server rejects the add.
   * @param {CustomEvent} _event
   */
  #handleCartError = (_event) => {
    const chip = this.#activeChip;
    if (!chip) return;
    this.#resetChip(chip);
    this.#activeChip = null;
  };

  /**
   * Shows the success checkmark optimistically and schedules an auto-reset.
   * Matches the ATC button's `data-added` pattern — fire-and-forget visual.
   * @param {HTMLButtonElement} chip
   */
  #setChipSuccess(chip) {
    this.#activeChip = chip;
    chip.classList.add('tap-chip--success');

    if (this.#successTimeout) clearTimeout(this.#successTimeout);
    this.#successTimeout = window.setTimeout(() => {
      this.#resetChip(chip);
      this.#activeChip = null;
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }

  /**
   * @param {HTMLButtonElement} chip
   */
  #resetChip(chip) {
    if (this.#successTimeout) clearTimeout(this.#successTimeout);
    chip.classList.remove('tap-chip--success');
  }
}

if (!customElements.get('quick-add-tap-chips')) {
  customElements.define('quick-add-tap-chips', QuickAddTapChips);
}

if (window.Shopify?.designMode) {
  /** @type {Record<string, 'overlay' | 'modal' | 'drawer'>} */
  const surfaceContentClassMap = {
    'quick-add-overlay-content': 'overlay',
    'quick-add-modal-content': 'modal',
    'quick-add-drawer-content': 'drawer',
  };
  const surfaceContentSelector = Object.keys(surfaceContentClassMap)
    .map((c) => `.${c}`)
    .join(',');

  /** @type {Set<HTMLElement>} */
  const pinnedSurfaces = new Set();
  /** @type {QuickAddDialog | null} */
  let pinnedDialog = null;

  /** @param {Element} target */
  function resolveSurfaceType(target) {
    const surfaceContent = target.closest(surfaceContentSelector);
    if (!surfaceContent) return null;
    for (const [className, type] of Object.entries(surfaceContentClassMap)) {
      if (surfaceContent.classList.contains(className)) return type;
    }
    return null;
  }

  function unpinAll() {
    for (const el of pinnedSurfaces) delete el.dataset.pinned;
    pinnedSurfaces.clear();
    if (pinnedDialog) delete pinnedDialog.dataset.designModeSurface;
    pinnedDialog = null;
  }

  /**
   * Find a card on the page that already has the given surface type open.
   * @param {'overlay' | 'modal' | 'drawer'} type
   * @returns {QuickAddBlock | null}
   */
  function findCardWithOpenSurface(type) {
    for (const card of document.querySelectorAll('quick-add-block')) {
      if (!(card instanceof QuickAddBlock)) continue;
      if (type === 'overlay') {
        const overlay = /** @type {HTMLElement | undefined} */ (card.refs.overlay);
        if (overlay && !overlay.hidden) return card;
        continue;
      }
      const dialog = card.querySelector('quick-add-dialog');
      if (!(dialog instanceof QuickAddDialog) || !dialog.refs.dialog?.open) continue;
      const isDrawer = card.dataset.mobileSurface === 'drawer' && window.matchMedia('(width < 64rem)').matches;
      const surfaceTypeOpen = isDrawer ? 'drawer' : 'modal';
      if (surfaceTypeOpen === type) return card;
    }
    return null;
  }

  /** @param {QuickAddBlock} block @param {'overlay' | 'modal' | 'drawer'} type */
  function pinSurfaceOnCard(block, type) {
    const surface = block.surface(type);
    if (!surface) return;
    surface.dataset.pinned = 'true';
    pinnedSurfaces.add(surface);
    if (surface instanceof QuickAddDialog) pinnedDialog = surface;
  }

  document.addEventListener('shopify:block:select', (/** @type {Event} */ event) => {
    const target = /** @type {Element | null} */ (event.target);
    if (!(target instanceof Element)) return;

    const surfaceType = resolveSurfaceType(target);
    unpinAll();
    if (!surfaceType) return;

    const isMobileViewport = window.matchMedia('(width < 64rem)').matches;
    if (surfaceType === 'drawer' && !isMobileViewport) return;
    if (surfaceType === 'overlay' && isMobileViewport) return;

    const selectedCard = target.closest('quick-add-block');
    if (!(selectedCard instanceof QuickAddBlock)) return;
    const alreadyOpen = findCardWithOpenSurface(surfaceType);
    const hostCard = alreadyOpen ?? selectedCard;

    hostCard.openSurface(surfaceType);
    pinSurfaceOnCard(hostCard, surfaceType);
  });

  document.addEventListener('shopify:block:deselect', () => {
    unpinAll();
  });
}
